/**
 * The GUI's copy of the core's graph.
 *
 * The core owns the graph; this module only mirrors it:
 *
 * 1. on every (re)connection it asks for `graph.snapshot`;
 * 2. then it applies each `graph.changed` event, in revision order;
 * 3. if a revision is missing (an event was lost), it asks for a new snapshot.
 *
 * Undo is per person: `graph.changed` carries the undo state of whoever made the change, so
 * this page only takes it from its own changes (it learns who it is from the snapshot). When
 * someone else replaces the whole graph, the core clears everyone else's history.
 *
 * Edits never change this copy directly: they are sent to the core, and the resulting
 * `graph.changed` event updates every GUI the same way, including the one that asked.
 *
 * Environments are spaces inside a project: the page works in one ({@link GraphMirror.setScope},
 * from `state/environments.ts`), and `state.graph` is that environment's part only (its nodes,
 * the links between them, its groups), so every window shows it without knowing about
 * environments. `full()` is the whole graph.
 */
import { createMemo, createSignal, type Accessor } from "solid-js";
import { createStore, produce, reconcile, type SetStoreFunction } from "solid-js/store";
import { categoriesOf } from "../shared/categories";
import type { Graph } from "@protocol/Graph";
import type { GraphChange } from "@protocol/GraphChange";
import type { Kinds } from "@protocol/Kinds";
import type { Link } from "@protocol/Link";
import type { Client } from "./client";

/** The mirrored state. */
export interface GraphState {
  /** The graph as the core last described it. */
  graph: Graph;
  /**
   * The kinds of the built-in plugins in use, as the core knows them (their rules apply and
   * their look is drawn besides the graph's own `graph.kinds`).
   */
  known: Kinds;
  /** The core's revision counter for this graph. */
  revision: number;
  /** Whether `graph.undo` would do something for this page (everyone has their own history). */
  canUndo: boolean;
  /** Whether `graph.redo` would do something for this page. */
  canRedo: boolean;
  /** `true` once the first snapshot arrived. */
  loaded: boolean;
  /** Goes up by one every time the graph shown changes (event, snapshot, or another environment worked in); handy to react on. */
  version: number;
  /**
   * Who made the last change (the core's id for them, see `presence.you()`); `null` after a
   * snapshot. Their page lays the change out for everyone (`state/sceneSync.ts`).
   */
  by: string | null;
}

/** `true` when two links join the same nodes with the same kind from the same pin (labels ignored), like the core. */
export function sameEdge(a: Pick<Link, "from" | "to" | "kind" | "pin">, b: Pick<Link, "from" | "to" | "kind" | "pin">): boolean {
  return a.from === b.from && a.to === b.to && a.kind === b.kind && (a.pin ?? null) === (b.pin ?? null);
}

/**
 * Applies one change to `graph`, exactly as the core did. Mutates `graph`.
 * Throws if the change does not fit (which means this copy drifted and needs a resync).
 */
export function applyChange(graph: Graph, change: GraphChange): void {
  /** Where a node is in the mirrored list (-1 if absent). */
  const nodeIndex = (id: string) => {
    const index = graph.nodes.findIndex((n) => n.id === id);
    if (index < 0) throw new Error(`node ${id} is not in the local copy`);
    return index;
  };
  /** Where a link is in the mirrored list (-1 if absent). */
  const linkIndex = (link: Link) => {
    const index = graph.links.findIndex((l) => sameEdge(l, link));
    if (index < 0) throw new Error(`link ${link.from} -${link.kind}-> ${link.to} is not in the local copy`);
    return index;
  };
  /** Where a group is in the mirrored list. */
  const groupIndex = (id: string) => {
    const index = graph.groups.findIndex((g) => g.id === id);
    if (index < 0) throw new Error(`group ${id} is not in the local copy`);
    return index;
  };
  switch (change.type) {
    case "node_added":
      graph.nodes.splice(Math.min(change.index, graph.nodes.length), 0, change.node);
      break;
    case "node_removed":
      graph.nodes.splice(nodeIndex(change.node.id), 1);
      break;
    case "node_updated":
      graph.nodes[nodeIndex(change.after.id)] = change.after;
      break;
    case "link_added":
      graph.links.splice(Math.min(change.index, graph.links.length), 0, change.link);
      break;
    case "link_removed":
      graph.links.splice(linkIndex(change.link), 1);
      break;
    case "link_updated":
      graph.links[linkIndex(change.before)] = change.after;
      break;
    case "group_added":
      graph.groups.splice(Math.min(change.index, graph.groups.length), 0, change.group);
      break;
    case "group_removed":
      graph.groups.splice(groupIndex(change.group.id), 1);
      break;
    case "group_updated":
      graph.groups[groupIndex(change.after.id)] = change.after;
      break;
    case "kinds_changed":
      graph.kinds = change.after;
      break;
    case "plugins_set":
      graph.plugins = change.after;
      break;
    case "workspaces_set":
      graph.workspaces = change.after;
      break;
    case "replaced":
      graph.nodes = change.after.nodes;
      graph.links = change.after.links;
      graph.groups = change.after.groups;
      graph.kinds = change.after.kinds;
      graph.plugins = change.after.plugins;
      graph.workspaces = change.after.workspaces;
      break;
  }
}

/** Whether `change` may change the plugins the project turned on (and so the known kinds). */
export function changesPlugins(change: GraphChange): boolean {
  return change.type === "plugins_set" || change.type === "replaced";
}

/**
 * The board a page works in: an environment (its id, and whether it is the first, which also
 * shows the nodes no environment claims) and, when it holds several, one of its workspaces.
 */
export interface GraphScope {
  /** The environment's id (its plugin's). */
  id: string;
  /** Whether it comes first in the project's list. */
  first: boolean;
  /** The workspace of the environment worked in (`null`: the environment is one board). */
  workspace?: string | null;
}

/** Whether a node or group placed in `environment` belongs to `scope`'s environment. */
export function inScope(environment: string | null | undefined, scope: GraphScope | null): boolean {
  if (!scope) return true;
  return environment ? environment === scope.id : scope.first;
}

/** The first workspace of `environment` in `graph` (where its items naming none sit), if it lists any. */
export function firstWorkspace(graph: Pick<Graph, "workspaces">, environment: string | null | undefined): string | null {
  return graph.workspaces?.find((w) => w.environment === environment)?.id ?? null;
}

/** The part of `graph` in `scope`: its nodes, the links between two of them, its groups (`null`: the whole graph). */
export function scopedGraph(graph: Graph, scope: GraphScope | null): Graph {
  if (!scope) return graph;
  const first = firstWorkspace(graph, scope.id);
  const here = (item: { environment?: string | null; workspace?: string | null }) =>
    inScope(item.environment, scope) && (!scope.workspace || (item.workspace ?? first) === scope.workspace);
  const nodes = graph.nodes.filter(here);
  const ids = new Set(nodes.map((n) => n.id));
  return { ...graph, nodes, links: graph.links.filter((l) => ids.has(l.from) && ids.has(l.to)), groups: graph.groups.filter(here) };
}

/** The data carried by a `graph.changed` event. */
interface Changed {
  revision: number;
  changes: GraphChange[];
  can_undo: boolean;
  can_redo: boolean;
  by: string;
}

/** The mirror plus a way to force a reload. */
export interface GraphMirror {
  /** Reactive state; read it in components, never write it. Its `graph` is the environment's part. */
  state: GraphState;
  /** The whole graph, every environment's part. */
  full: Accessor<Graph>;
  /** Works in environment `scope` (and its workspace) from now on (`null`: the whole graph, a project with none). */
  setScope(scope: GraphScope | null): void;
  /** The environment worked in. */
  scope: Accessor<GraphScope | null>;
  /** The graph's categories, in the order they first appear (which picks their colours). */
  categories: Accessor<string[]>;
  /** Asks the core for a fresh snapshot. */
  resync(): Promise<void>;
}

/** Starts mirroring the core's graph through `client`. */
export function createGraphMirror(client: Client): GraphMirror {
  const [inner, setState] = createStore<GraphState>({
    graph: { nodes: [], links: [], groups: [] },
    known: { nodes: [], links: [] },
    revision: 0,
    canUndo: false,
    canRedo: false,
    loaded: false,
    version: 0,
    by: null,
  });
  const [scope, setScopeSignal] = createSignal<GraphScope | null>(null, { equals: (a, b) => a?.id === b?.id && a?.first === b?.first && (a?.workspace ?? null) === (b?.workspace ?? null) });
  /** The environment's part of the graph; the store itself while there is no environment (fine-grained). */
  const scoped = createMemo(() => scopedGraph(inner.graph, scope()));
  /** What windows read: the store, with the environment's part as `graph`. */
  const state: GraphState = {
    get graph() {
      return scoped();
    },
    get known() {
      return inner.known;
    },
    get revision() {
      return inner.revision;
    },
    get canUndo() {
      return inner.canUndo;
    },
    get canRedo() {
      return inner.canRedo;
    },
    get loaded() {
      return inner.loaded;
    },
    get version() {
      return inner.version;
    },
    get by() {
      return inner.by;
    },
  };
  let loading = false;
  let buffered: Changed[] = [];
  /** Who this page is, as the last snapshot said (`null`: an older core, whose undo is shared). */
  let you: string | null = null;

  /** Applies a `graph.changed` event, or resyncs when a revision was missed. */
  const handle = (changed: Changed) => {
    if (loading) {
      buffered.push(changed);
      return;
    }
    if (changed.revision <= state.revision) return; // already part of the snapshot
    if (changed.revision !== state.revision + 1) {
      void resync(); // an event was missed
      return;
    }
    try {
      apply(setState, changed, you);
    } catch {
      void resync();
      return;
    }
    // Other plugins on: other known kinds, which only the snapshot carries.
    if (changed.changes.some(changesPlugins)) void resync();
  };

  /** Replaces the mirror with a fresh snapshot from the core. */
  async function resync(): Promise<void> {
    loading = true;
    buffered = [];
    try {
      const response = await client.request({ method: "graph.snapshot" });
      if (response.type === "graph") {
        you = response.you ?? null;
        // `reconcile` keeps unchanged nodes as the same objects, so windows only redraw what changed.
        setState("graph", reconcile(response.graph, { key: "id", merge: true }));
        setState("known", reconcile(response.known ?? { nodes: [], links: [] }));
        setState({
          revision: response.revision,
          canUndo: response.can_undo,
          canRedo: response.can_redo,
          loaded: true,
          version: state.version + 1,
          by: null,
        });
      }
    } catch {
      // Disconnected: the next onOpen triggers a new resync.
    } finally {
      loading = false;
      const pending = buffered.sort((a, b) => a.revision - b.revision);
      buffered = [];
      for (const changed of pending) handle(changed);
    }
  }

  client.onEvent((event) => {
    if (event.event === "graph.changed") handle(event.data);
  });
  client.onOpen(() => void resync());

  const categories = createMemo(() => categoriesOf(state.graph), [], { equals: (a, b) => a.length === b.length && a.every((c, i) => c === b[i]) });
  /** Works in another environment: what is shown changes, so `version` goes up (the scene and the diagram follow it). */
  const setScope = (next: GraphScope | null) => {
    const before = scope();
    setScopeSignal(next);
    if (scope() !== before) setState("version", (v) => v + 1);
  };
  return { state, categories, resync, full: () => inner.graph, setScope, scope };
}

/**
 * Applies a `graph.changed` event to the store in one batch. The undo state is taken only from
 * this page's own changes (`you`); someone else replacing the whole graph cleared it.
 */
function apply(setState: SetStoreFunction<GraphState>, changed: Changed, you: string | null): void {
  setState(
    produce((s) => {
      for (const change of changed.changes) applyChange(s.graph, change);
      s.revision = changed.revision;
      if (you === null || changed.by === you) {
        s.canUndo = changed.can_undo;
        s.canRedo = changed.can_redo;
      } else if (changed.changes.some((c) => c.type === "replaced")) {
        s.canUndo = s.canRedo = false;
      }
      s.by = changed.by;
      s.version += 1;
    }),
  );
}
